import useSWR from 'swr';
import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Step from '@mui/material/Step';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import StepLabel from '@mui/material/StepLabel';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// Parcelamento → Cadastrar (wizard). Consome o motor:
//  GET  /parcelamento/debitos   (aba Lançamentos)
//  GET  /parcelamento/regras    (aba Cadastro)
//  POST /parcelamento/simular   (preview, não persiste)
//  POST /parcelamento/executar  (efetiva)
// ----------------------------------------------------------------------

interface Debito {
  id: number;
  parcela: number | null;
  receita: string | null;
  exercicio: string | null;
  valor_original: number;
  vencimento: string | null;
  juros: number;
  multa: number;
  total: number;
}
interface Regra {
  id: number;
  nome: string;
  max_parcelas: number;
  min_parcelas: number;
}
interface Preview {
  numparcelas: number;
  total_original: number;
  total_juros: number;
  total_multa: number;
  total_com_desconto: number;
  valor_parcela: number;
}

const STEPS = ['Lançamentos', 'Cadastro', 'Confirmar'];

export default function ParcelamentoNovoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [regraId, setRegraId] = useState<number | ''>('');
  const [numparcelas, setNumparcelas] = useState<number>(2);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: debitosData, isLoading: loadingDebitos } = useSWR<{ debitos: Debito[] }>(
    '/portal-api/parcelamento/debitos',
    fetcher
  );
  const { data: regrasData } = useSWR<{ regras: Regra[] }>('/portal-api/parcelamento/regras', fetcher);
  const debitos = useMemo(() => debitosData?.debitos ?? [], [debitosData]);
  const regras = regrasData?.regras ?? [];

  const regra = regras.find((r) => r.id === regraId);
  const totalSelecionado = debitos.filter((d) => sel.has(d.id)).reduce((s, d) => s + d.total, 0);

  const toggle = (id: number) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleTodos = () =>
    setSel((prev) => (prev.size === debitos.length ? new Set() : new Set(debitos.map((d) => d.id))));

  const parcelasInvalidas =
    !regra ||
    numparcelas < 1 ||
    (regra.max_parcelas > 0 && numparcelas > regra.max_parcelas) ||
    (regra.min_parcelas > 0 && numparcelas < regra.min_parcelas);

  const payload = () => ({ fkparcelamento: regraId, numparcelas, debitos: [...sel] });

  const simular = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('/portal-api/parcelamento/simular', payload());
      setPreview(res.data as Preview);
      setStep(2);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Falha ao simular o parcelamento.');
    } finally {
      setLoading(false);
    }
  };

  const executar = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('/portal-api/parcelamento/executar', payload());
      toast.success(`Parcelamento efetivado${res.data?.numero ? ` — nº ${res.data.numero}` : ''}.`);
      navigate('/parcelamento');
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Falha ao efetivar o parcelamento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading="Cadastrar Parcelamento"
        links={[{ name: 'Parcelamento', href: '/parcelamento' }, { name: 'Cadastrar' }]}
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      <Card sx={{ p: 3 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Passo 1 — seleção de débitos */}
        {step === 0 && (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {sel.size} de {debitos.length} débito(s) · Total {formatCurrency(totalSelecionado)}
              </Typography>
              <Button size="small" onClick={toggleTodos} disabled={debitos.length === 0}>
                {sel.size === debitos.length && debitos.length > 0 ? 'Limpar' : 'Selecionar todos'}
              </Button>
            </Stack>
            <Box sx={{ maxHeight: 380, overflow: 'auto', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Receita</TableCell>
                    <TableCell>Exerc.</TableCell>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debitos.map((d) => (
                    <TableRow key={d.id} hover selected={sel.has(d.id)} onClick={() => toggle(d.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={sel.has(d.id)} />
                      </TableCell>
                      <TableCell>{d.receita ?? '—'}</TableCell>
                      <TableCell>{d.exercicio ?? '—'}</TableCell>
                      <TableCell>{d.parcela ?? '—'}</TableCell>
                      <TableCell>{formatDateBr(d.vencimento)}</TableCell>
                      <TableCell align="right">{formatCurrency(d.total)}</TableCell>
                    </TableRow>
                  ))}
                  {debitos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        {loadingDebitos ? 'Carregando...' : 'Nenhum débito aberto para parcelar.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button variant="contained" disabled={sel.size === 0} onClick={() => setStep(1)}>
                Próximo
              </Button>
            </Stack>
          </>
        )}

        {/* Passo 2 — regra + parcelas */}
        {step === 1 && (
          <>
            <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
              <TextField
                select
                label="Regra de parcelamento"
                value={regraId}
                onChange={(e) => setRegraId(e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
              >
                {regras.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.nome}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                label="Número de parcelas"
                value={numparcelas}
                onChange={(e) => setNumparcelas(Number(e.target.value))}
                fullWidth
                helperText={
                  regra
                    ? `Entre ${regra.min_parcelas || 1} e ${regra.max_parcelas} parcelas`
                    : 'Escolha a regra primeiro'
                }
                error={!!regra && parcelasInvalidas}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total selecionado: <strong>{formatCurrency(totalSelecionado)}</strong> em {sel.size} débito(s).
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
              <Button color="inherit" onClick={() => setStep(0)}>
                Voltar
              </Button>
              <Button variant="contained" disabled={parcelasInvalidas || loading} onClick={simular}>
                Simular
              </Button>
            </Stack>
          </>
        )}

        {/* Passo 3 — preview + confirmar */}
        {step === 2 && preview && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Simulação — {regra?.nome}
            </Typography>
            <Grid container spacing={2.5}>
              <Campo label="Débitos" value={`${sel.size}`} />
              <Campo label="Total original" value={formatCurrency(preview.total_original)} />
              <Campo label="Juros" value={formatCurrency(preview.total_juros)} />
              <Campo label="Multa" value={formatCurrency(preview.total_multa)} />
              <Campo label="Total com desconto" value={formatCurrency(preview.total_com_desconto)} />
              <Campo label="Nº de parcelas" value={`${preview.numparcelas}x`} />
              <Campo label="Valor da parcela" value={formatCurrency(preview.valor_parcela)} destaque />
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Ao confirmar, o acordo é criado, as parcelas são geradas e os débitos originais entram em
              parcelamento. Esta ação é definitiva.
            </Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
              <Button color="inherit" onClick={() => setStep(1)} disabled={loading}>
                Voltar
              </Button>
              <Button variant="contained" color="success" disabled={loading} onClick={executar}>
                Confirmar parcelamento
              </Button>
            </Stack>
          </>
        )}
      </Card>
    </DashboardContent>
  );
}

function Campo({ label, value, destaque }: { label: string; value: React.ReactNode; destaque?: boolean }) {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant={destaque ? 'subtitle1' : 'body2'} sx={{ fontWeight: destaque ? 700 : 500, color: destaque ? 'success.main' : 'text.primary' }}>
        {value}
      </Typography>
    </Grid>
  );
}
